import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  usePatientMedicalDataLazyQuery,
  usePatientMedicalHistoryTimelineLazyQuery,
  usePatientProblemsLazyQuery,
} from '~/api/graphql/generated/types'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getPatientId } from '~/store/reducers/soap'

import { transformMedicalHistoryData } from './transformMedicalHistoryData'
import { useMedicalHistoryDateRange } from './useMedicalHistoryDateRange'

/**
 * Hook to provide metadata for audio recorder session initialization.
 * Fetches medical history from GraphQL queries (timeline, patient data, problems).
 *
 * Note: Medical/discharge notes are NOT included here - they are sent
 * separately via onBeforeSummarize callback for summarization context.
 *
 * @returns Object with medicalHistory (stringified JSON) and isMedicalHistoryReady flag
 *          isMedicalHistoryReady is true when all queries complete (success or failure)
 */
export const useAudioRecorderMetadata = () => {
  const businessId = useSelector(getCurrentBusinessId)
  const patientId = useSelector(getPatientId)
  const [medicalHistory, setMedicalHistory] = useState<string>('')
  const [isMedicalHistoryReady, setIsMedicalHistoryReady] = useState(false)

  // Track which patientId the current query data belongs to
  // Prevents race condition where stale data from previous patient is marked as ready
  const dataPatientIdRef = useRef<string | null>(null)

  // Date range configuration (default: last year)
  const dateRange = useMedicalHistoryDateRange('lastYear')

  // Lazy query hooks for all three queries
  const [fetchTimeline, { data: timelineData, error: timelineError }] =
    usePatientMedicalHistoryTimelineLazyQuery()
  const [fetchPatientData, { data: patientData, error: patientError }] =
    usePatientMedicalDataLazyQuery()
  const [fetchProblems, { data: problemsData, error: problemsError }] =
    usePatientProblemsLazyQuery()

  // Fetch medical history when patientId/businessId changes
  useEffect(() => {
    if (!patientId || !businessId) {
      setMedicalHistory('')
      setIsMedicalHistoryReady(false) // NOT ready if no patientId/businessId - prevents session init with empty metadata
      dataPatientIdRef.current = null
      return
    }

    setIsMedicalHistoryReady(false) // Reset ready state when fetching new patient
    setMedicalHistory('') // Clear stale data immediately when patientId changes
    // Track which patientId we're fetching for
    dataPatientIdRef.current = patientId

    // Execute all three queries in parallel
    // Note: Lazy queries return promises, but data comes through hook state
    // We'll handle completion in a separate effect
    if (fetchTimeline) {
      fetchTimeline({
        variables: {
          patientId,
          from: dateRange.from,
          to: dateRange.to,
        },
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch timeline data', error)
      })
    }

    if (fetchPatientData) {
      fetchPatientData({
        variables: {
          patientId,
          businessId,
        },
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch patient data', error)
      })
    }

    if (fetchProblems) {
      fetchProblems({
        variables: {
          patientId,
        },
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch problems data', error)
      })
    }
  }, [
    businessId,
    patientId,
    dateRange.from,
    dateRange.to,
    fetchTimeline,
    fetchPatientData,
    fetchProblems,
  ])

  // Handle query results when they arrive
  useEffect(() => {
    // Check if all queries have completed (either with data or error)
    // We consider a query "complete" if:
    // 1. It has data, OR
    // 2. It has an error (failed query)
    // 3. We've attempted to fetch (patientId/businessId exist)
    if (!patientId || !businessId) {
      return
    }

    // Race condition fix: Only process data if it belongs to the current patientId
    // When patientId changes, the first effect fires new queries, but this effect
    // might still see stale data from the previous patient in the same render cycle.
    // By checking dataPatientIdRef, we ensure we only mark as ready when data
    // matches the patientId that triggered the queries.
    if (dataPatientIdRef.current !== patientId) {
      return
    }

    // Additional safety check: verify patient data ID matches current patientId
    // This prevents processing stale data even if ref check passes
    // If patientData exists but doesn't match current patientId, don't process
    // This check must happen BEFORE checking if queries are complete, otherwise
    // we might mark stale data as ready
    if (
      patientData !== undefined &&
      patientData.patient?.id !== undefined &&
      patientData.patient.id !== patientId
    ) {
      // We have patientData but it doesn't match current patientId - this is stale data
      return
    }

    const timelineComplete =
      timelineData !== undefined || timelineError !== undefined
    // Only consider patient query complete if data matches current patientId
    // This prevents stale data from being marked as complete
    const patientComplete =
      (patientData !== undefined && patientData.patient?.id === patientId) ||
      patientError !== undefined
    const problemsComplete =
      problemsData !== undefined || problemsError !== undefined

    if (timelineComplete && patientComplete && problemsComplete) {
      // All queries have completed, transform and stringify the data
      const transformedData = transformMedicalHistoryData(
        timelineData?.timeline,
        patientData?.patient,
        problemsData?.problemCatalog,
      )
      const stringifiedData = JSON.stringify(transformedData)

      setMedicalHistory(stringifiedData)
      setIsMedicalHistoryReady(true)
    }
  }, [
    patientId,
    businessId,
    timelineData,
    patientData,
    problemsData,
    timelineError,
    patientError,
    problemsError,
  ])

  return {
    medicalHistory,
    isMedicalHistoryReady,
  }
}
